import { useFormik } from 'formik'
import * as Yup from 'yup'

import { useLocation, useNavigate } from 'react-router-dom'
import { Footer, PasswordHelper, Wrapper } from 'components'
import { Button, Card, Input, Spinner, toast } from 'keep-react'
import { useState } from 'react'
import { Eye, EyeSlash } from 'phosphor-react'
import { authService } from 'api/services/auth'
import { RouteNamesNonAuth } from 'routes/enum'

const validationSchema = Yup.object().shape({
  password: Yup.string()
    .required('Este campo é obrigatório')
    .min(10, 'A password deve ter pelo menos 10 caracteres')
    .matches(/[A-Z]/, 'A password deve conter pelo menos uma letra maiúscula')
    .matches(/[a-z]/, 'A password deve conter pelo menos uma letra minúscula')
    .matches(/[0-9]/, 'A password deve conter pelo menos um número')
    .matches(
      /[!@#$%^&*(),.?":{}|<>]/,
      'A password deve conter pelo menos um símbolo especial',
    ),
  confirmPassword: Yup.string()
    .required('Este campo é obrigatório')
    .oneOf([Yup.ref('password')], 'Passwords têm de ser iguais'),
  nif: Yup.number().test(
    'len',
    'O NIF deve ter 9 caracteres',
    val => !val || val.toString().length === 9,
  ),
  phoneNumber: Yup.string(),
  username: Yup.string().required('Este campo é obrigatório'),
})

export const RegisterUser = () => {
  const [isCallingApi, setIsCallingApi] = useState<boolean>(false)
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const inviteId = params.get('inviteId')
  const email = params.get('email')
  const [passwordFieldType, setPasswordFieldType] = useState<
    'password' | 'text'
  >('password')
  const [confirmPasswordFieldType, setConfirmPasswordFieldType] = useState<
    'password' | 'text'
  >('password')
  const navigate = useNavigate()

  const formik = useFormik({
    initialValues: {
      username: '',
      nif: '',
      phoneNumber: '',
      password: '',
      confirmPassword: '',
    },
    validateOnBlur: false,
    validateOnChange: true,
    validationSchema: validationSchema,
    onSubmit: async values => {
      setIsCallingApi(true)
      const body = {
        password: values.password,
        phone: values.phoneNumber,
        invite: inviteId || '',
        name: values.username,
        nif: String(values.nif),
      }

      try {
        await authService.register(body)
        toast.success('Utilizador registado com sucesso.')
        navigate(RouteNamesNonAuth.LOGIN)
      } catch (error) {
        console.error('Error registering user:', error)
      } finally {
        setIsCallingApi(false)
      }
    },
  })

  if (!inviteId || !email) return null

  return (
    <div
      className="w-screen h-screen bg-cover bg-no-repeat bg-center flex items-center"
      style={{
        backgroundImage: `url(images/dark_broker.jpg)`,
      }}
    >
      <Wrapper>
        <div className="flex flex-col items-center m-auto gap-x-4 gap-y-12 max-w-[700px] h-full">
          <h1 className="text-white uppercase font-semibold text-2xl text-center tracking-wider">
            Seguros à velocidade de um foguetão
          </h1>
          <div className="grid grid-cols-2 gap-12 items-start w-full">
            <Card className="w-full bg-white/70 p-4 rounded-xl max-w-md mx-auto">
              <h2 className="font-light text-heading-6 text-primary-400 pb-4 uppercase">
                Registo Utilizador
              </h2>
              <p className="text-body-4 pb-8">
                Foi convidado para o Portal InnovaGo
              </p>
              <form onSubmit={formik.handleSubmit}>
                <div className="space-y-5">
                  <Input
                    type="email"
                    name="mail"
                    onChange={formik.handleChange}
                    placeholder="Email"
                    className="px-2"
                    disabled={true}
                    defaultValue={email}
                  />

                  <Input
                    type="text"
                    name="username"
                    onChange={formik.handleChange}
                    placeholder="Nome e Apelido"
                    className="px-2"
                    value={formik.values.username}
                    required
                  />
                  {formik.touched.username && formik.errors.username && (
                    <div className="text-red-600 text-sm">
                      {formik.errors.username}
                    </div>
                  )}

                  <Input
                    type="number"
                    name="nif"
                    onChange={formik.handleChange}
                    placeholder="NIF"
                    className="px-2"
                    value={formik.values.nif}
                    minLength={9}
                    maxLength={9}
                  />
                  {formik.touched.nif && formik.errors.nif && (
                    <div className="text-red-600 text-sm">
                      {formik.errors.nif}
                    </div>
                  )}

                  <Input
                    type="text"
                    name="phoneNumber"
                    onChange={formik.handleChange}
                    placeholder="Número de Telefone"
                    className="px-2"
                    value={formik.values.phoneNumber}
                  />
                  {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                    <div className="text-red-600 text-sm">
                      {formik.errors.phoneNumber}
                    </div>
                  )}

                  <div className="relative">
                    <Input
                      type={passwordFieldType}
                      name="password"
                      onChange={formik.handleChange}
                      placeholder="Password"
                      className="px-2"
                      value={formik.values.password}
                    />
                    <Button
                      type="button"
                      shape="icon"
                      color="secondary"
                      className="absolute top-1/2 right-3 -translate-y-1/2 p-0 text-metal-900 bg-transparent hover:bg-transparent"
                      onClick={() => {
                        if (passwordFieldType === 'password') {
                          setPasswordFieldType('text')
                        } else {
                          setPasswordFieldType('password')
                        }
                      }}
                    >
                      {passwordFieldType === 'password' ? (
                        <Eye size={16} />
                      ) : (
                        <EyeSlash size={16} />
                      )}
                    </Button>
                  </div>
                  {formik.touched.password && formik.errors.password && (
                    <div className="text-red-600 text-sm">
                      {formik.errors.password}
                    </div>
                  )}
                  <div className="relative">
                    <Input
                      type={confirmPasswordFieldType}
                      name="confirmPassword"
                      onChange={formik.handleChange}
                      placeholder="Confirmar a Password"
                      className="px-2"
                      value={formik.values.confirmPassword}
                    />
                    <Button
                      type="button"
                      shape="icon"
                      color="secondary"
                      className="absolute top-1/2 right-3 -translate-y-1/2 p-0 text-metal-900 bg-transparent hover:bg-transparent"
                      onClick={() => {
                        if (confirmPasswordFieldType === 'password') {
                          setConfirmPasswordFieldType('text')
                        } else {
                          setConfirmPasswordFieldType('password')
                        }
                      }}
                    >
                      {confirmPasswordFieldType === 'password' ? (
                        <Eye size={16} />
                      ) : (
                        <EyeSlash size={16} />
                      )}
                    </Button>
                  </div>
                  {formik.touched.confirmPassword &&
                    formik.errors.confirmPassword && (
                      <div className="text-red-600 text-sm">
                        {formik.errors.confirmPassword}
                      </div>
                    )}

                  <Button
                    className="w-full gap-4"
                    type="submit"
                    disabled={isCallingApi}
                  >
                    {isCallingApi && <Spinner />}
                    {isCallingApi ? 'A registar...' : 'Registar'}
                  </Button>

                  <p className="text-right pt-2">
                    <Button
                      variant="link"
                      onClick={() => navigate(RouteNamesNonAuth.LOGIN)}
                    >
                      Login
                    </Button>
                  </p>
                </div>
              </form>
            </Card>

            <Card className="w-full bg-white/70 p-4 rounded-xl max-w-md mx-auto">
              <div className="flex flex-col gap-4 items-center justify-center">
                <PasswordHelper password={formik.values.password} />
              </div>
            </Card>
          </div>
        </div>
      </Wrapper>
      <Footer />
    </div>
  )
}

export default RegisterUser
